import type { WorkspaceDto, WorkspaceKind } from '@dbrain/shared';
import { Button, Dialog, Field, IconButton, Icons, TextInput } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCapabilities } from '../../experience.js';
import { useNavigate } from 'react-router';
import { api } from '../../api/client.js';
import { keys } from '../../api/hooks.js';
import { homeOf, useMeta } from '../../experience.js';
import { ErrorAlert } from '../ErrorAlert.js';

/** A new chat space or code workspace: kind, name, then straight into it. */
export function NewWorkspaceDialog({ initialKind = 'chat', onClose }: { initialKind?: WorkspaceKind; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const meta = useMeta();
  const capabilities = useCapabilities();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<WorkspaceKind>(capabilities.codeExperience ? initialKind : 'chat');
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => api<WorkspaceDto>('POST', '/api/workspaces', { name: name.trim(), kind }),
    onSuccess: (workspace) => {
      void queryClient.invalidateQueries({ queryKey: keys.dashboard });
      onClose();
      // a workspace of the other experience opens on its own address
      const origin = homeOf(meta.data, workspace.kind);
      if (origin) window.location.assign(`${origin}/w/${workspace.id}`);
      else void navigate(`/w/${workspace.id}`);
    },
  });
  return (
    <Dialog title={t('workspaces.new.title')} closeLabel={t('common.close')} onClose={onClose} data-testid="new-workspace-dialog">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <ErrorAlert error={create.error} />
        {capabilities.codeExperience ? (
        <fieldset className="choice-cards">
          <legend className="ui-field__label">{t('workspaces.new.kind')}</legend>
          {(['chat', 'code'] as const).map((value) => (
            <label key={value} className={kind === value ? 'choice-card choice-card--selected' : 'choice-card'}>
              <input type="radio" name="workspace-kind" value={value} checked={kind === value} onChange={() => setKind(value)} data-testid={`new-workspace-kind-${value}`} />
              <span className={`choice-card__icon choice-card__icon--${value}`} aria-hidden="true">
                {value === 'chat' ? <Icons.conversations size={20} /> : <Icons.code size={20} />}
              </span>
              <span className="choice-card__text">
                <strong>{t(`kinds.${value}`)}</strong>
                <span>{t(`workspaces.new.hint.${value}`)}</span>
              </span>
            </label>
          ))}
        </fieldset>
        ) : null}
        <Field label={t('dashboard.workspaces.name')}>
          {({ id, describedBy }) => (
            <TextInput id={id} aria-describedby={describedBy} required value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="new-workspace-name" />
          )}
        </Field>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" busy={create.isPending} disabled={!name.trim()} data-testid="new-workspace-submit">
            {t('common.create')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function NewWorkspaceButton({ iconOnly = false, kind, testId }: { iconOnly?: boolean; kind?: WorkspaceKind; testId?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const label = kind ? t(kind === 'chat' ? 'dashboard.workspaces.newChat' : 'dashboard.workspaces.newCode') : t('shell.newWorkspace');
  return (
    <>
      {iconOnly ? (
        <IconButton size="sm" label={label} icon={<Icons.plus />} onClick={() => setOpen(true)} data-testid={testId ?? 'sidebar-new-workspace'} />
      ) : (
        <Button size="sm" variant="secondary" icon={<Icons.plus size={16} />} onClick={() => setOpen(true)} data-testid={testId}>
          {label}
        </Button>
      )}
      {open ? <NewWorkspaceDialog initialKind={kind} onClose={() => setOpen(false)} /> : null}
    </>
  );
}
